// El botón «Volver a correr el análisis» de la ficha, con su confirmación. Presentacional: el
// request y la selección de la fila nueva los pone ModeliaPage.
import { useId, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button, ConfirmDialog } from '@/kit';
import { resolveRerunGate } from '../logic';
import type { ModeliaEvaluationRow } from '../types';

export const RERUN_LABEL = 'Volver a correr el análisis';

/** Qué pasa al confirmar. Se dice ANTES, para que la fila extra no se lea como un duplicado. */
export const RERUN_CONFIRM_NOTE =
    'Se agrega una evaluación nueva con la fecha de hoy. La actual no se reemplaza: se queda en la lista como historial.';

/** Lo que confirma que la fila extra es intencional, no un duplicado. */
export const RERUN_SUCCESS_NOTE =
    'Se agregó una evaluación nueva y quedó seleccionada. La anterior sigue en la lista como historial.';

export interface ModeliaRerunActionProps {
    row: ModeliaEvaluationRow;
    /** Sin `loan.scoring.write` el botón no se pinta: el endpoint responde 403 y tocar no lo arregla. */
    granted: boolean;
    isRunning: boolean;
    onConfirm: (evaluationId: string) => void;
}

export function ModeliaRerunAction({
    row,
    granted,
    isRunning,
    onConfirm,
}: ModeliaRerunActionProps) {
    const [confirming, setConfirming] = useState(false);
    const reasonId = useId();
    const { canRerun, blockedReason } = resolveRerunGate(row);

    if (!granted) return null;

    return (
        <div className="flex flex-col items-start gap-1">
            {/* `aria-disabled` y NO `disabled`: un control deshabilitado de verdad no anuncia su
                motivo ni recibe foco, así que el operador nunca se entera de por qué no puede. */}
            <Button
                type="button"
                variant="outline"
                size="sm"
                aria-disabled={!canRerun || isRunning}
                aria-describedby={blockedReason === null ? undefined : reasonId}
                isLoading={isRunning}
                leftIcon={<RefreshCw className="size-3.5" aria-hidden />}
                className={canRerun ? undefined : 'cursor-not-allowed opacity-60'}
                onClick={() => {
                    if (!canRerun || isRunning) return;
                    setConfirming(true);
                }}
            >
                {RERUN_LABEL}
            </Button>

            {blockedReason === null ? null : (
                <p id={reasonId} className="max-w-[46ch] text-[10.5px] text-muted-foreground">
                    {blockedReason}
                </p>
            )}

            <ConfirmDialog
                open={confirming}
                onOpenChange={setConfirming}
                title={RERUN_LABEL}
                description={RERUN_CONFIRM_NOTE}
                confirmLabel="Volver a correr"
                loading={isRunning}
                onConfirm={() => {
                    setConfirming(false);
                    onConfirm(row.evaluation.evaluationId);
                }}
            />
        </div>
    );
}
